import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "../Firebase";

const SEP1_2026  = new Date("2026-09-01T00:00:00");
const SEP15_2026 = new Date("2026-09-15T23:59:59");

const parseDate = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const fmtDate = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const compactPhone = (val) => String(val || "").replace(/\D/g, "").slice(-10);
const cv = (val, fb = "-") => (val === null || val === undefined || val === "" ? fb : String(val));

export default function WorkshopUserWise() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                // Fetch all three collections in parallel
                const [wsSnap, glowSnap, funderSnap] = await Promise.all([
                    getDocs(collection(db, "workshop_registration")),
                    getDocs(collection(db, "nv_glow_membership")),
                    getDocs(collection(db, "funder_users_list")),
                ]);

                // Build glow membership map: phone → [{start, end}]
                const glowMap = {};
                glowSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no || docSnap.id);
                    if (!phone) return;
                    if (!glowMap[phone]) glowMap[phone] = [];
                    glowMap[phone].push({
                        start: parseDate(d.start_date),
                        end: parseDate(d.end_date),
                    });
                });

                // Build funder map: phone → [{start, end}]
                const funderMap = {};
                funderSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no || docSnap.id);
                    if (!phone) return;
                    if (!funderMap[phone]) funderMap[phone] = [];
                    funderMap[phone].push({
                        start: parseDate(d.start_date),
                        end: parseDate(d.end_date),
                    });
                });

                const data = [];
                wsSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const dt = parseDate(d.created_date);
                    if (!dt || dt < SEP1_2026 || dt > SEP15_2026) return;

                    const phone = compactPhone(d.phone);
                    const wDate = parseDate(d.workshop_Date);

                    // Check if phone was an active Glow member at the time of workshop
                    const glowPeriods = glowMap[phone] || [];
                    const wasMember = wDate
                        ? glowPeriods.some(p => p.start && p.end && wDate >= p.start && wDate <= p.end)
                        : false;

                    const funderPeriods = funderMap[phone] || [];
                    const isPartnerUser = wDate
                        ? funderPeriods.some(p => p.start && p.end && wDate >= p.start && wDate <= p.end)
                        : false;

                    data.push({
                        id: docSnap.id,
                        name: cv(d.full_name),
                        phone,
                        workshopDate: d.workshop_Date || null,
                        createdDate: d.created_date || null,
                        wasMember,
                        isPartnerUser,
                    });
                });

                data.sort((a, b) =>
                    (parseDate(b.workshopDate)?.getTime() || 0) - (parseDate(a.workshopDate)?.getTime() || 0)
                );
                setRows(data);
            } catch (err) {
                console.error("WorkshopUserWise fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const handleExport = () => {
        const sheetData = filtered.map((r, i) => ({
            "#": i + 1,
            "Name": r.name,
            "Phone No": r.phone,
            "Workshop Date": fmtDate(r.workshopDate),
            "Glow Member at Workshop Time": r.wasMember ? "Member" : "Not a Member",
            "User Type": r.isPartnerUser ? "Partner User" : "App User",
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetData), "Workshop Users");
        XLSX.writeFile(wb, "workshop_user_wise.xlsx");
    };

    const filtered = rows.filter(r => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return r.name.toLowerCase().includes(q) || r.phone.includes(q);
    });

    return (
        <div className="container-fluid py-4">

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h3 className="mb-1">Workshop Registrations</h3>
                    <div className="text-muted" style={{ fontSize: "13px" }}>
                        workshop_registration · 1 Sep 2026 – 15 Sep 2026 · {rows.length} records
                    </div>
                </div>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            {/* SEARCH */}
            <div className="card mb-3">
                <div className="card-body py-3 px-4">
                    <div className="d-flex gap-3 align-items-center">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by name or phone…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ maxWidth: "300px" }}
                        />
                        {search && (
                            <span className="text-muted" style={{ fontSize: "13px" }}>
                                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                            </span>
                        )}
                        <button
                            className="btn btn-success ms-auto"
                            onClick={handleExport}
                            disabled={loading || filtered.length === 0}
                            style={{ minWidth: "140px", fontWeight: 700 }}
                        >
                            Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* TABLE */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="mt-2 text-muted" style={{ fontSize: "14px" }}>Loading…</div>
                </div>
            ) : (
                <div className="card">
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                            <thead className="table-dark">
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Phone No</th>
                                    <th>Workshop Date</th>
                                    <th className="text-center">Glow Member at Workshop Time</th>
                                    <th className="text-center">User Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4 text-muted">
                                            No data found for 1–15 Sep 2026
                                        </td>
                                    </tr>
                                ) : filtered.map((row, i) => (
                                    <tr key={row.id}>
                                        <td>{i + 1}</td>
                                        <td style={{ fontWeight: 600 }}>{row.name}</td>
                                        <td>{row.phone}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.workshopDate)}</td>
                                        <td className="text-center">
                                            <span
                                                className={`badge ${row.wasMember ? "bg-success" : "bg-secondary"}`}
                                                style={{ fontSize: "12px", padding: "5px 10px" }}
                                            >
                                                {row.wasMember ? "✓ Member" : "Not a Member"}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <span
                                                className={`badge ${row.isPartnerUser ? "bg-warning text-dark" : "bg-primary"}`}
                                                style={{ fontSize: "12px", padding: "5px 10px" }}
                                            >
                                                {row.isPartnerUser ? "Partner User" : "App User"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
